import * as React from 'react';
import Box from '@mui/material/Box';
import { GRID_CHECKBOX_SELECTION_FIELD } from '@mui/x-data-grid';
import {
  DataGridPro,
  GridColDef,
  GridToolbarContainer,
  GridToolbarExport,
  GridLoadingOverlayProps,
  LoadingOverlayPropsOverrides,
  GridRenderCellParams,
  GridRowSelectionModel,
  GridToolbarProps,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import {
  Button,
  CircularProgress,
  debounce,
  LinearProgress,
} from '@mui/material';
import HeaderElipse from './headerElipse';
import RowButton from './rowButton';
import DropDown from './dropDown';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { useDispatch, useSelector } from '../../state';
import { openPackingSlipModal } from '../../state/packingSlipModalSlice';
import { topNavBarHeight } from '../NavBars/constants';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import type { PackingSlipSearchInput } from '@repo/api-contract';
import {
  searchPackingSlips,
  setSearchInput,
  setLoading,
} from '../../state/packingSlipBrowseSlice';
import {
  GRID_CHECKBOX_COLUMN_ORDER,
  usePersistedDataGridColumnPreferences,
} from '../../util/hooks';
import type { DataGridTableId } from '../../util/dataGridTableIds';
import { DataGridTranslatedColumnHeader } from '../dataGrid/DataGridTranslatedColumnHeader';

const TABLE_ID = 'browse-packing-slips' satisfies DataGridTableId;

const PACKING_SLIPS_COL_I18N = 'team.Components.PackingSlipsBrowseTable';

interface LoaderProps
  extends Omit<GridLoadingOverlayProps, 'color'>,
    LoadingOverlayPropsOverrides {
  color?: string;
}

const Loader = (props: LoaderProps) => {
  return <LinearProgress color={'primary'} />;
};

function CustomToolbar(
  props: GridToolbarProps & { selectedRows?: GridRowSelectionModel },
) {
  const { selectedRows = [] } = props;
  const dispatch = useDispatch();
  const { t: tGB } = useTranslation([], { keyPrefix: 'team.Global.Buttons' });
  const { t: tCO } = useTranslation([], { keyPrefix: PACKING_SLIPS_COL_I18N });
  const { searchInput, searchBy, loading } = useSelector(
    (store) => store.packingSlipBrowseSlice,
  );
  const { team } = useSelector((store) => store.userSlice);

  const debouncedSearch = useCallback(
    debounce((searchQuery: string) => {
      const payload: PackingSlipSearchInput = {
        query: searchQuery,
        searchBy,
        size: 100,
        page: 0,
      };
      dispatch(searchPackingSlips(payload));
    }, 1500),
    [searchBy],
  );

  if (team === 'init' || team === 'loading') return null;

  return (
    <GridToolbarContainer>
      <DropDown />
      <TextField
        value={searchInput}
        onChange={(e) => {
          dispatch(setLoading(true));
          dispatch(setSearchInput(e.currentTarget.value));
          debouncedSearch(e.currentTarget.value);
        }}
        id="filled-basic"
        label={tCO('Search for Packing Slips...')}
        variant="outlined"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchOutlinedIcon />
            </InputAdornment>
          ),
          endAdornment: (
            <React.Fragment>
              {loading ? <CircularProgress color="inherit" size={20} /> : null}
            </React.Fragment>
          ),
        }}
      />
      <Button
        variant="outlined"
        color="primary"
        onClick={() => {
          const payload: PackingSlipSearchInput = {
            query: searchInput,
            searchBy,
            size: 100,
            page: 0,
          };
          dispatch(searchPackingSlips(payload));
        }}
      >
        {tGB('Search')}
      </Button>
      <Box sx={{ flexGrow: 1 }} />
      <button
        className="text-blue-600 border-0 bg-inherit hover:cursor-pointer"
        onClick={() => {
          dispatch(openPackingSlipModal({ resetState: true }));
        }}
      >
        {tGB('+ ADD')}
      </button>
      <GridToolbarExport
        slotProps={{
          tooltip: { title: 'Export data' },
        }}
      />
      <HeaderElipse selectedRows={selectedRows} />
    </GridToolbarContainer>
  );
}

export default function PackingSlipsBrowseTable() {
  const dispatch = useDispatch();
  const { team } = useSelector((store) => store.userSlice);
  const { results, loading, searchBy } = useSelector(
    (store) => store.packingSlipBrowseSlice,
  );
  const apiRef = useGridApiRef();
  const [rowSelectionModel, setRowSelectionModel] =
    React.useState<GridRowSelectionModel>([]);

  const initialResults = async () => {
    if (team === 'init' || team === 'loading') return null;
    const payload: PackingSlipSearchInput = {
      query: '',
      searchBy,
      size: 100,
      page: 0,
    };
    await dispatch(searchPackingSlips(payload));
  };

  React.useEffect(() => {
    initialResults();
  }, []);

  const baseColumns = React.useMemo((): GridColDef[] => {
    return [
      {
        field: 'packingSlipNo',
        type: 'string',
        flex: 1,
        headerName: 'Packing Slip No.',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Packing Slip No."
            keyPrefix={PACKING_SLIPS_COL_I18N}
            params={params}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{params.value}</span>
        ),
      },
      {
        field: 'shipDate',
        flex: 1,
        headerName: 'Ship Date',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Ship Date"
            keyPrefix={PACKING_SLIPS_COL_I18N}
            params={params}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => {
          if (!params.value) return '';
          return dayjs(params.value).format('MM/DD/YYYY');
        },
      },
      {
        field: 'customerId',
        flex: 1,
        headerName: 'Customer Id',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Customer Id"
            keyPrefix={PACKING_SLIPS_COL_I18N}
            params={params}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{params.value}</span>
        ),
      },
      {
        field: 'customerRef',
        flex: 1,
        headerName: 'Customer Ref',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Customer Ref"
            keyPrefix={PACKING_SLIPS_COL_I18N}
            params={params}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => {
          return <span>{params.row.customerRef}</span>;
        },
      },
      {
        field: 'trackingNo',
        flex: 1,
        headerName: 'Tracking No',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Tracking No"
            keyPrefix={PACKING_SLIPS_COL_I18N}
            params={params}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{params.value}</span>
        ),
      },
      {
        field: 'action',
        headerName: '',
        width: 55,
        resizable: false,
        sortable: false,
        renderHeader: () => null,
        renderCell: (params) => {
          return <RowButton packingSlip={params.row}></RowButton>;
        },
      },
    ];
  }, []);

  const persistedPrefs = usePersistedDataGridColumnPreferences({
    tableId: TABLE_ID,
    columns: baseColumns,
    apiRef,
    leadingGridColumnFields: GRID_CHECKBOX_COLUMN_ORDER,
  });

  const columns = React.useMemo(
    () => persistedPrefs.applyPersistedPreferences(baseColumns),
    [baseColumns, persistedPrefs.applyPersistedPreferences],
  );

  return (
    <div style={{ height: `calc(100vh - ${topNavBarHeight} - 250px)` }}>
      <DataGridPro
        apiRef={apiRef}
        columnHeaderHeight={56}
        sx={{
          border: 'none',
          padding: '1rem',
          '.MuiDataGrid-row:nth-of-type(even)': {
            backgroundColor: '#ECECEC;',
          },
          '.MuiDataGrid-scrollbar--horizontal': { display: 'none' },
          '.MuiDataGrid-columnHeaderTitle': {
            fontWeight: 'bold',
            whiteSpace: 'normal',
            lineHeight: 1.2,
          },
        }}
        rows={
          results
            ? results.results.map((result) => {
                return {
                  id: result.PackingSlipId,
                  packingSlipNo: result.code,
                  shipDate: result.shipDate,
                  trackingNo: result.trackingNumber,
                  customerRef: result.customerReference,
                  customerId: result.customer.CustomerId,
                  createdAt: result.createdAt,
                  packinSlip: result,
                };
              })
            : []
        }
        loading={loading}
        columns={columns}
        columnVisibilityModel={persistedPrefs.columnVisibilityModel}
        onColumnVisibilityModelChange={
          persistedPrefs.onColumnVisibilityModelChange
        }
        onColumnWidthChange={persistedPrefs.onColumnWidthChange}
        onColumnOrderChange={persistedPrefs.onColumnOrderChange}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 100,
            },
          },
        }}
        pinnedColumns={{
          right: ['action'],
          left: [GRID_CHECKBOX_SELECTION_FIELD],
        }}
        pageSizeOptions={[1]}
        checkboxSelection
        disableRowSelectionOnClick
        autoHeight={false}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={setRowSelectionModel}
        slots={{
          loadingOverlay: Loader,
          toolbar: CustomToolbar,
        }}
        slotProps={{
          toolbar: { selectedRows: rowSelectionModel },
        }}
      />
    </div>
  );
}
